<!--
  NewOrderFlow -- staff "Add New Order": pick a store, say how the order is
  paid, fill in the AddOrder form.

  Card: Rename "Admin Buyer" to Client Portal and retire the legacy Admin
  Buyer dashboard (Bug Intake #566, https://trello.com/c/9R4TXtuz). The
  legacy /admin-buyer/dashboard was the only home of staff order entry; it
  is deleted, and this ONE flow is mounted by both new entry points:
    - the distributor Orders page (DistributorNewOrderPage.vue), and
    - the Client Portal Orders page (pages/portal/NewOrder.vue).
  Both reuse AddOrder.vue unchanged, so there is one order-entry and one
  pricing path (server-quoted totals, #4857/#4866), never a fork per surface.

  The entry page owns WHICH stores are offered (the server's list for the
  caller); this component never widens it: a requested store
  (`initialStoreId`, from a route query or the portal's header scope) is
  selected only when it is one of `stores`.

  Whether a store can take an order comes from the store record itself
  (`getCampaignById`): a complete store is locked, and a store is ready only
  when the server says `is_ready === true` (an absent answer is treated as
  not ready, never as ready). The same two rules the dashboard applied.
-->
<template>
  <div class="new-order-flow" data-testid="new-order-flow">
    <v-autocomplete
      :model-value="storeId"
      :items="stores"
      item-title="name"
      item-value="id"
      :loading="storesLoading"
      :disabled="storesLoading"
      :no-data-text="storesLoading ? 'Loading... Please wait' : 'No stores available'"
      label="Store"
      variant="outlined"
      density="comfortable"
      clearable
      class="new-order-flow__store"
      data-testid="new-order-store-select"
      @update:model-value="pickStore"
    >
      <template #item="{ props: itemProps }">
        <v-list-item v-bind="itemProps" data-testid="new-order-store-option" />
      </template>
    </v-autocomplete>

    <p
      v-if="storeId === null"
      class="new-order-flow__hint"
      data-testid="new-order-pick-hint"
    >
      Choose the store this order is for.
    </p>

    <v-progress-linear v-else-if="loadingStore" indeterminate color="primary" />

    <v-alert
      v-else-if="storeError"
      type="error"
      variant="tonal"
      data-testid="new-order-store-error"
    >
      We could not load this store. Choose it again, or reload the page.
    </v-alert>

    <v-alert
      v-else-if="blockedReason === 'locked'"
      type="info"
      variant="tonal"
      data-testid="new-order-store-locked"
    >
      This store is locked, so new orders can no longer be added to it.
    </v-alert>

    <v-alert
      v-else-if="blockedReason === 'not_ready'"
      type="info"
      variant="tonal"
      data-testid="new-order-store-not-ready"
    >
      This store is not ready to accept orders yet.
    </v-alert>

    <template v-else-if="campaign">
      <add-order
        v-if="paymentChoice !== null"
        :key="`${storeId}-${paymentChoice}`"
        :storeID="storeId"
        :campaign="campaign"
        :displayCreditCardReader="paymentChoice === 'card'"
        @exit="$emit('exit')"
      />
    </template>
  </div>
</template>

<script>
import axios from "@/axios";
import { describeError } from "@/utils/safeErrorLog";
import AddOrder from "./AddOrder.vue";

// A card-tender store. Staff are NOT offered "Invoice" on it (card #622): that
// choice placed a card order with no tender and no saved card, which nothing
// collects and which is held at batch release (`no_saved_payment_method`). The
// server refuses that order too (`_refuse_unpaid_card_charge`). A card store
// therefore goes straight to the form with the card reader.
const CARD_PAYMENT_METHOD = "Individual CC Payment";

export default {
  name: "NewOrderFlow",
  components: { AddOrder },
  props: {
    // The stores the caller may place an order for: [{ id, name }]. The
    // entry page loads them from the server; this component never adds one.
    stores: { type: Array, default: () => [] },
    storesLoading: { type: Boolean, default: false },
    // A store to start on (route query string or a numeric id). Honored only
    // when it is one of `stores`.
    initialStoreId: { type: [Number, String], default: null },
  },
  emits: ["exit"],
  data() {
    return {
      storeId: null,
      campaign: null,
      loadingStore: false,
      storeError: false,
      // null until the store loads: 'invoice' (a store that takes no card) | 'card'.
      paymentChoice: null,
      // Monotonic request id: only the newest store load may write state.
      reqSeq: 0,
      // Set once the user (or the preselect) has chosen, so a later prop
      // change never overrides a deliberate pick.
      picked: false,
    };
  },
  computed: {
    blockedReason() {
      if (!this.campaign) return null;
      if (this.campaign.complete) return "locked";
      if (this.campaign.is_ready !== true) return "not_ready";
      return null;
    },
  },
  watch: {
    stores: { handler: "preselect", immediate: true },
    initialStoreId: "preselect",
  },
  methods: {
    offeredId(raw) {
      if (raw === null || raw === undefined || raw === "") return null;
      const text = String(raw).trim();
      if (!/^\d+$/.test(text)) return null;
      const id = Number(text);
      return this.stores.some((s) => Number(s.id) === id) ? id : null;
    },
    preselect() {
      if (this.picked) return;
      let id = this.offeredId(this.initialStoreId);
      if (id === null && this.stores.length === 1) {
        id = this.offeredId(this.stores[0].id);
      }
      if (id !== null) this.pickStore(id);
    },
    pickStore(raw) {
      this.picked = true;
      this.reqSeq += 1;
      this.campaign = null;
      this.storeError = false;
      this.paymentChoice = null;
      this.loadingStore = false;
      const id = this.offeredId(raw);
      this.storeId = id;
      if (id !== null) this.loadStore(id);
    },
    async loadStore(id) {
      const seq = this.reqSeq;
      this.loadingStore = true;
      try {
        const resp = await axios.get(`/campaign/getCampaignById/${id}`);
        if (seq !== this.reqSeq) return;
        this.campaign = (resp && resp.data) || null;
        if (!this.campaign) {
          this.storeError = true;
          return;
        }
        if (this.blockedReason) return;
        this.paymentChoice =
          this.campaign.payment_method === CARD_PAYMENT_METHOD ? "card" : "invoice";
      } catch (err) {
        if (seq !== this.reqSeq) return;
        console.error("new order: store load failed", describeError(err));
        this.storeError = true;
      } finally {
        if (seq === this.reqSeq) this.loadingStore = false;
      }
    },
  },
};
</script>

<style scoped>
.new-order-flow__store {
  max-width: 480px;
}
.new-order-flow__hint {
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
}
</style>
